/**
 * ─── [P7A §2] AT WHICH LEVEL WAS THIS ORDER RAISED? ─────────────────────────────────────────
 *
 * Every order carries `raisedAtLevel` on the wire: the level of its RAISER's effective unit —
 * `raised_at_org_unit_id ?? created_by_org_unit_id`, the acting unit when the raiser was elevated
 * ([C5c §11f]) and the posting otherwise. It replaces [C5c §10]'s boolean `raisedAtCommissionerate`,
 * so the fact has ONE vocabulary: a boolean could say "HQ" and nothing else, and the client asked
 * for three words.
 *
 * ⚠️ BY POSITION, WITH THE `type` LABEL AS A BELT — `raiserLevelOf` in
 * `api/src/utils/orgUnitPosition.util.ts` is the one resolver:
 *  - `HQ`       — the commissionerate root (`isCommissionerateRoot`: no ancestors AND the label);
 *  - `DIVISION` — one level below it AND labelled `DIVISION` (a wing is a division, [WINGS]);
 *  - `UNIT`     — any other unit that resolves.
 * A label a divisional nodal can rewrite on their own node ([C1e §5]) can therefore only make a
 * row UNDER-claim: a division relabelled `COMMISSIONERATE` reads `UNIT`, never `HQ`.
 *
 * ⚠️ `null` IS NOT A FOURTH LEVEL. It means the raiser's unit id resolves to no org unit (a
 * dangling id — a hand edit on Atlas, a purged demo unit), and every client renders NO pill for
 * it, never a blank one.
 *
 * ⚠️ `DIVISION` IS A LEVEL, NOT THE DRO UNIT. The client's word for it is "DRO", which in this code
 * already names a division's reserve-office UNIT (`DRO-<DIV>`, `UNIT`/`RESERVE_FORCE`). A DRO
 * unit's nodal cannot raise an order unelevated, and elevated one up they act AT the division —
 * so their order reads `DIVISION` for its level, not for the DRO. The web's native `title` and
 * Duty Mitra's `raiser.title.*` ("Raised at division level") are what tell the two apart.
 *
 * ⚠️ AND IT IS NOT `NODAL_LEVELS` (`HQ · DIVISION · PS`). That names a PERSON by their node's
 * TYPE ([C4a §8]); this names an ORDER by its raiser's POSITION, with `UNIT` rather than `PS`.
 * Two facts, so two tuples — deliberately.
 *
 * Here rather than in a schema because Duty Mitra needs it at RUN time (`@pams/shared/constants`
 * is the only runtime barrel its budget admits — [P5b]).
 */
export const RAISER_LEVELS = ['HQ', 'DIVISION', 'UNIT'] as const;
export type RaiserLevel = (typeof RAISER_LEVELS)[number];
